import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useSupportMessengerBoot } from "@/lib/support";
import {
  applySupportLauncherPlacement,
  initialSupportLauncherPlacement,
  isSamePlacement,
  publishSupportLauncherCssPlacement,
  supportLauncherMove,
  supportLauncherPlacement,
  type SupportLauncherPlacement,
  type SupportLauncherSurroundings,
} from "@/lib/support-launcher-placement";
import { useCompanyPreviewPanelEntry } from "@/stores/company-preview-panel-store";
import { useHasModalOverlay } from "@/stores/modal-overlay-store";
import { useTallestObstruction } from "@/stores/support-launcher-store";
import { useEffect, useRef } from "react";

/**
 * Settling delay before an `update()` that moves the launcher — each one pings
 * Intercom's servers, and a surface being resized re-measures on every frame of
 * the drag. Hiding and showing it never waits: see
 * {@link useSupportLauncherPlacement}.
 *
 * Nothing is kept waiting by it: the bubble is already where this placement
 * puts it, by CSS (see {@link publishSupportLauncherCssPlacement}). What
 * settles here is what Intercom itself believes, which is only read when the
 * messenger unfolds against the launcher.
 */
const UPDATE_DEBOUNCE_MS = 200;

/**
 * Keeps the native Intercom launcher placed against whatever currently shares
 * the corner it floats in — see {@link supportLauncherPlacement} for the rules.
 */
export function useSupportLauncherPlacement(): void {
  const bootCount = useSupportMessengerBoot();
  const { isMobile } = useDeviceFormat();
  const obstruction = useTallestObstruction();
  const hasModalOverlay = useHasModalOverlay();
  // `isVisible` is the sheet's own open condition: the launcher only moves for
  // a panel that is actually on screen.
  const { state, isVisible } = useCompanyPreviewPanelEntry();
  const visiblePanel = isVisible ? state : null;
  const isDesktop = !isMobile;

  // An "inline" panel is drawn by its host container, not over the viewport
  // edge, so it never reaches the corner the launcher sits in.
  const hasPreviewSheet = visiblePanel?.surface === "sheet";

  const { isHidden, verticalPadding, horizontalPadding } =
    supportLauncherPlacement({
      isDesktop,
      obstruction,
      hasPreviewSheet,
      hasModalOverlay,
    });

  const surroundingsRef = useRef<SupportLauncherSurroundings | null>(null);

  // Both launchers are placed by CSS, not by the settings — the pre-load one
  // because it ignores them, the real one because the `update()` that carries
  // them has to settle first, and a bubble that only moves once a panel has
  // finished sliding reads as a glitch rather than as a move. So the position
  // is published on the spot, and the surroundings it came from are kept to
  // ease each leg of the move at the pace of the surface that caused it.
  //
  // A surrounding can change without moving the launcher — an obstruction
  // growing within its own rounding step, most of all. The publication is what
  // tells the two apart: it times a leg only when that leg's own position
  // changed.
  useEffect(() => {
    const surroundings = {
      isDesktop,
      obstruction,
      hasPreviewSheet,
      hasModalOverlay,
    };
    const move = supportLauncherMove(surroundingsRef.current, surroundings);
    surroundingsRef.current = surroundings;

    publishSupportLauncherCssPlacement(
      { isHidden, verticalPadding, horizontalPadding },
      move,
    );
  }, [
    isDesktop,
    obstruction,
    hasPreviewSheet,
    hasModalOverlay,
    isHidden,
    verticalPadding,
    horizontalPadding,
  ]);

  const appliedRef = useRef<SupportLauncherPlacement | null>(null);
  const appliedBootRef = useRef(0);

  useEffect(() => {
    // Before the boot there is no messenger to talk to; the boot itself already
    // carries the right initial placement.
    if (bootCount === 0) {
      appliedRef.current = null;
      appliedBootRef.current = 0;
      return;
    }

    // Every boot puts the launcher back at the placement it was handed, and the
    // messenger re-boots in place whenever its identity changes — switching
    // teams, for one. That happens behind this driver's back, so a boot resets
    // what it believes is applied; otherwise the launcher would sit at the boot
    // padding while the driver, seeing its own last move unchanged, never
    // touches it again.
    if (appliedBootRef.current !== bootCount) {
      appliedBootRef.current = bootCount;
      appliedRef.current = initialSupportLauncherPlacement(isDesktop);
    }

    const placement = { isHidden, verticalPadding, horizontalPadding };
    const applied = appliedRef.current;
    if (isSamePlacement(applied, placement)) return;

    // Hiding the launcher is the one change that cannot wait: an overlay opens
    // in one step, and the whole point is that the bubble must not be floating
    // over it — 200 ms of a clickable bubble on top of a modal is the bug. It
    // cannot come in bursts either, unlike a dragged panel's re-measures the
    // debounce exists for, so only a move settles first.
    if (applied === null || applied.isHidden !== placement.isHidden) {
      appliedRef.current = placement;
      applySupportLauncherPlacement(placement);
      return;
    }

    const timeout = setTimeout(() => {
      appliedRef.current = placement;
      applySupportLauncherPlacement(placement);
    }, UPDATE_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [bootCount, isDesktop, isHidden, verticalPadding, horizontalPadding]);
}
